import { mt, MutationData } from '@passionware/monads';
import { AlertCircle } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from 'v5/design-system/Alert';
import { OverflowTooltip } from 'v5/design-system/Tooltip';

export interface DialogErrorFooterProps {
  /**
   * Mutation data for handling error states
   */
  mutation?: MutationData<unknown, unknown>;

  /**
   * Custom error message to display (overrides mutation error)
   */
  error?: string | ReactNode;

  /**
   * Custom error title (defaults to "Error")
   */
  errorTitle?: string;

  /**
   * Function to render error messages (defaults to error.message)
   */
  renderError?: (error: Error) => ReactNode;

  /**
   * Compact single-line layout for narrow dialogs (icon + message, no title)
   */
  compact?: boolean;
}

export function DialogErrorFooter({
  mutation,
  error,
  errorTitle,
  renderError = (error: Error) => error.message,
  compact = false
}: DialogErrorFooterProps) {
  const { t } = useTranslation('patterns');
  const defaultErrorTitle = t('DialogErrorFooter.error');
  const displayErrorTitle = errorTitle || defaultErrorTitle;

  if (compact) {
    const message = error
      ? error
      : mutation && mt.isInError(mutation)
        ? renderError(mutation.error)
        : null;

    if (!message) {
      return null;
    }

    return (
      <Alert
        variant="destructive"
        layout="horizontal"
        className="min-w-0 flex-1 overflow-hidden"
      >
        <AlertCircle />
        {typeof message === 'string' ? (
          <OverflowTooltip title={message} align="start">
            <AlertDescription className="block min-w-0 truncate text-sm">
              {message}
            </AlertDescription>
          </OverflowTooltip>
        ) : (
          <AlertDescription className="block min-w-0 truncate text-sm">
            {message}
          </AlertDescription>
        )}
      </Alert>
    );
  }

  // Show custom error if provided
  if (error) {
    return (
      <Alert variant="destructive" className="min-w-0 w-full">
        <AlertCircle />
        <AlertTitle>{displayErrorTitle}</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Show mutation error if available
  if (mutation && mt.isInError(mutation)) {
    return (
      <Alert variant="destructive" className="min-w-0 w-full">
        <AlertCircle />
        <AlertTitle>{displayErrorTitle}</AlertTitle>
        <AlertDescription>{renderError(mutation.error)}</AlertDescription>
      </Alert>
    );
  }

  // No error to display
  return null;
}
